/**
 * Stale-bundle (chunk load) recovery primitives.
 *
 * Invariants (audit RESEARCH_RESILIENCE_RELEASE_DATASPINE.md §2.3):
 * - Only real chunk-load failures count as "stale bundle". A ReferenceError or
 *   TypeError is a code defect; reloading cannot fix it and only loops.
 * - At most ONE automatic reload per (tab, build id). The budget is recorded in
 *   sessionStorage *before* reloading; if storage cannot be read or written the
 *   answer is "no automatic reload".
 * - Never unregister the service worker (that drops push subscriptions); ask it
 *   to update instead and clear only this app's runtime caches.
 */

// public/index.html duplicates these three values (it runs before the bundle);
// src/__tests__/preReactRecovery.test.js asserts they stay identical.
export const CHUNK_ERROR_PATTERN =
  /Loading (?:CSS )?chunk [\w-]+ failed|ChunkLoadError|dynamically imported module/i;

export const RECOVERY_KEY_PREFIX = "investing-os-chunk-recovery:";

const APP_CACHE_PREFIX = "investing-os-";
const SERVICE_WORKER_UPDATE_TIMEOUT_MS = 3000;
export const MAIN_BUNDLE_PATTERN = /\/static\/js\/main\.([0-9a-f]+)\.js(?:$|\?)/i;

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  return CHUNK_ERROR_PATTERN.test(String(error.message || ""));
}

/**
 * Identify the running build from the content hash CRA puts in the main bundle
 * filename (changes on every deploy whose code changed). public/index.html
 * derives the same id so both recovery paths share one budget per build.
 */
export function getBuildId(doc = typeof document !== "undefined" ? document : null) {
  try {
    const scripts = doc ? Array.from(doc.querySelectorAll("script[src]")) : [];
    for (const script of scripts) {
      const match = MAIN_BUNDLE_PATTERN.exec(script.getAttribute("src") || script.src || "");
      if (match) return match[1];
    }
  } catch {
    // fall through
  }
  return "unknown";
}

function defaultStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Spend the single automatic-reload budget for this build. Returns true only
 * if the budget was unspent AND the reservation was durably recorded.
 */
export function reserveAutoReload(buildId = getBuildId(), storage = defaultStorage()) {
  if (!storage) return false;
  const key = `${RECOVERY_KEY_PREFIX}${buildId}`;
  try {
    if (storage.getItem(key) != null) return false;
    storage.setItem(key, String(Date.now()));
    return storage.getItem(key) != null;
  } catch {
    return false;
  }
}

/**
 * Best-effort cleanup before a reload: drop this app's runtime caches and ask
 * the service worker to check for an update. Never throws, never unregisters.
 */
export async function refreshAppCaches() {
  try {
    if (typeof window !== "undefined" && "caches" in window && window.caches) {
      const keys = await window.caches.keys();
      await Promise.all(
        keys.filter((key) => key.startsWith(APP_CACHE_PREFIX)).map((key) => window.caches.delete(key)),
      );
    }
  } catch (error) {
    console.error("[chunkRecovery] cache cleanup failed", error);
  }

  try {
    const registration =
      typeof navigator !== "undefined" && navigator.serviceWorker
        ? await navigator.serviceWorker.getRegistration()
        : null;
    if (registration && typeof registration.update === "function") {
      let timer;
      try {
        await Promise.race([
          Promise.resolve().then(() => registration.update()),
          new Promise((resolve) => {
            timer = window.setTimeout(resolve, SERVICE_WORKER_UPDATE_TIMEOUT_MS);
          }),
        ]);
      } finally {
        window.clearTimeout(timer);
      }
    }
  } catch (error) {
    console.error("[chunkRecovery] service worker update failed", error);
  }
}
